import FEATURE_DEFINITIONS from "../../../Data/FeatureDefinitions.json";

const getValue = (item, key) => String(item?.[key] || "").trim();

const cleanText = (value = "") => String(value || "").trim();

const normalizeFeatureName = (value = "") => {
  return cleanText(value)
    .toLowerCase()
    .replace(/[™®ᴹᶜᴹᴰ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const groupFeaturesByProductType = (data = FEATURE_DEFINITIONS) => {
  return data.reduce((acc, item) => {
    const productTypeEn =
      item.productType?.en || getValue(item, "product-type-en") || "Other";

    const productTypeFr =
      item.productType?.fr || getValue(item, "product-type-fr");

    const featureEn =
      item.feature?.en || getValue(item, "feature-en");

    const featureFr =
      item.feature?.fr || getValue(item, "feature-fr");

    if (!acc[productTypeEn]) {
      acc[productTypeEn] = {
        productTypeEn,
        productTypeFr,
        features: [],
      };
    }

    acc[productTypeEn].features.push({
      featureEn,
      featureFr,
      definitionEn:
        item.definition?.en || getValue(item, "definition-en"),
      definitionFr:
        item.definition?.fr || getValue(item, "definition-fr"),
    });

    return acc;
  }, {});
};

export const printFeaturesByProductType = (data = FEATURE_DEFINITIONS) => {
  const grouped = groupFeaturesByProductType(data);

  Object.values(grouped).forEach((group) => {
    console.log(`\n=== ${group.productTypeEn} / ${group.productTypeFr} ===`);

    group.features.forEach((item, index) => {
      console.log(`${index + 1}. ${item.featureEn} / ${item.featureFr}`);
    });
  });

  return grouped;
};

export const getFeatureAuditList = (data = FEATURE_DEFINITIONS) => {
  const grouped = groupFeaturesByProductType(data);

  return Object.values(grouped).map((group) => ({
    productTypeEn: group.productTypeEn,
    productTypeFr: group.productTypeFr,
    featureCount: group.features.length,
    features: group.features.map((item) => ({
      en: item.featureEn,
      fr: item.featureFr,
    })),
  }));
};

/* ---------------------------------------------------------
   Shared Feature Definition Audit
   Shows features used by more than one product type.
--------------------------------------------------------- */

export const getSharedFeatureDefinitionsAudit = (data = FEATURE_DEFINITIONS) => {
  const groupedByFeature = data.reduce((acc, item) => {
    const productTypeEn =
      item.productType?.en || getValue(item, "product-type-en") || "Other";

    const productTypeFr =
      item.productType?.fr || getValue(item, "product-type-fr");

    const featureEn =
      item.feature?.en || getValue(item, "feature-en");

    const featureFr =
      item.feature?.fr || getValue(item, "feature-fr");

    const definitionEn =
      item.definition?.en || getValue(item, "definition-en");

    const definitionFr =
      item.definition?.fr || getValue(item, "definition-fr");

    const normalizedFeature = normalizeFeatureName(featureEn);

    if (!normalizedFeature) return acc;

    if (!acc[normalizedFeature]) {
      acc[normalizedFeature] = {
        normalizedFeature,
        featureNames: new Set(),
        rows: [],
      };
    }

    acc[normalizedFeature].featureNames.add(featureEn);

    acc[normalizedFeature].rows.push({
      productTypeEn,
      productTypeFr,
      featureEn,
      featureFr,
      definitionEn,
      definitionFr,
    });

    return acc;
  }, {});

  return Object.values(groupedByFeature)
    .filter((group) => {
      const uniqueTypes = new Set(group.rows.map((row) => row.productTypeEn));
      return uniqueTypes.size > 1;
    })
    .map((group) => ({
      normalizedFeature: group.normalizedFeature,
      featureNames: Array.from(group.featureNames),
      sharedAcrossTypes: [
        ...new Set(group.rows.map((row) => row.productTypeEn)),
      ],
      count: group.rows.length,
      rows: group.rows,
    }))
    .sort((a, b) => b.count - a.count);
};

export const getSharedFeatureDefinitionsTableRows = (
  data = FEATURE_DEFINITIONS
) => {
  const audit = getSharedFeatureDefinitionsAudit(data);

  return audit.flatMap((group) =>
    group.rows.map((row) => ({
      feature: row.featureEn,
      productType: row.productTypeEn,
      definitionPreview: row.definitionEn.slice(0, 160),
    }))
  );
};